import './index.css'
import cn from 'clsx'

import React, { useEffect, useRef, useState } from 'react'
import { MdPhotoSizeSelectLarge } from '@react-icons/all-files/md/MdPhotoSizeSelectLarge'
import ButtonContainer from 'components/ui/button-container'
import Icon from 'components/ui/icon'
import Tooltip from 'components/ui/tooltip'
import FileDropWrapper from 'containers/FileDropWrapper'

// TODO:
// 1. Update progress
// 2. Probably rename onDrop to onChange because it is used in RemoveImageButton
const getBackgroundStyle = (
  imageUrl: string | ArrayBuffer,
  style?: Record<string, any>,
) => ({
  backgroundImage: `url("${imageUrl}")`,
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  ...style,
})

const getCaption = (files: string[], caption: string): string => {
  if (files.length > 1) {
    return `${files[0]} + ${files.length - 1} files`
  }

  if (files.length > 0) {
    return files[0] ?? caption
  }

  return caption
}

type Props = {
  children?: React.ReactNode
  caption?: string[]
  compact?: boolean
  helpText?: string
  image?: string | null | undefined | ArrayBuffer
  height?: number
  multiple?: boolean
  previewStyle?: Record<string, any>
  showPreview?: boolean
  type?: string
  dnd?: boolean
  onDrop: (file: any) => void
  onPreview?: (image: string) => void
  onClick?: () => any
  showRemove?: boolean
  limit?: number
  rounded?: boolean
}

/**
 * A file selection component with drag-and-drop support, image previews, and multiple file handling
 * @param children - Additional content to render in the file selector
 * @param caption - Array of captions for different states [add, change]
 * @param compact - Whether to use compact styling (defaults to false)
 * @param helpText - Additional help text to display
 * @param image - The image URL or data to display as preview
 * @param height - Custom height for the file selector
 * @param multiple - Whether to allow multiple file selection
 * @param previewStyle - Additional styles for the image preview
 * @param showPreview - Whether to show image preview (defaults to false)
 * @param type - The type of files to select (e.g., 'image')
 * @param dnd - Whether to enable drag-and-drop functionality
 * @param onDrop - Callback fired when files are dropped or selected
 * @param onPreview - Callback fired when image preview is generated
 * @param onClick - Callback fired when the selector is clicked
 * @param showRemove - Whether to show the remove button (defaults to true)
 * @param limit - Maximum number of files that can be selected
 * @param rounded - Whether to use rounded corners in drag-and-drop mode
 * @returns A file selector component with various display modes and functionality
 */
const FileSelector = (
  props: Props,
): React.ReactNode | React.ReactElement<'div'> => {
  // SLAM-1943: Defaults moved to params because React 19 ignores defaultProps on function components.
  const { showRemove = true } = props
  const inputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<string[]>([])
  const [preview, setPreview] = useState<string | ArrayBuffer | null>(null)
  useEffect(() => {
    setPreview(props.image ?? null)
  }, [props.image])

  const onDrop = (files: File[]) => {
    if (props.type === 'image' && props.showPreview) {
      const first = files[0]
      if (!first || first.type.indexOf('image/') !== 0) return
      processImageFiles(files)
    }

    setFiles(files.map(file => file.name))
    props.onDrop(files)
  }

  const processImageFiles = (files: File[]) => {
    const file = files[0]
    if (!file) return

    // show preview without triggering server upload
    const reader = new FileReader()

    reader.onload = event => {
      if (props.onPreview) {
        // @ts-expect-error
        props.onPreview(event.target.result)
      }

      setPreview(event.target?.result ?? null)
    }

    reader.readAsDataURL(file)
  }

  const changeImage = () => {
    const cancelBubble = (props.onClick && props.onClick()) === false

    if (inputRef.current && !cancelBubble) {
      inputRef.current.click()
    }
  }

  const removeImage = (event: React.SyntheticEvent<any>) => {
    event.preventDefault()
    event.stopPropagation()
    setFiles([])
    setPreview(null)
    props.onDrop(null)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files: File[] = []
    const emptyFiles: File[] = []
    const fileList = event.target.files

    if (!fileList) return

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i]

      if (!file) continue

      if (props.limit && props.limit < i + 1) {
        break
      }

      if (file.size > 0) {
        files.push(file)
      } else {
        emptyFiles.push(file)
      }
    }

    if (emptyFiles.length > 0) {
      // openEmptyFilesDialog(emptyFiles)
    }

    if (files.length > 0) {
      onDrop(files)
    }

    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const renderUploadArea = () => {
    let placeholder
    placeholder =
      props.type === 'image'
        ? ['Click to add an image', 'Click to change image']
        : ['Click to add file', 'Click to change file']

    if (props.dnd) {
      placeholder = new Array(2).fill('Drag files here, or click to browse')
    }

    const caption = props.caption
      ? props.caption[preview ? 1 : 0]
      : placeholder[preview ? 1 : 0]

    const ChangeImageButton = () => (
      <Tooltip content="Change image">
        <button className="file-selector-button" onClick={changeImage}>
          <Icon type="image-add" width="24" height="24" />
        </button>
      </Tooltip>
    )

    const EditButtons = () => (
      <ButtonContainer>
        {!props.dnd && <ChangeImageButton />}
        {showRemove && (
          <Tooltip content="Remove image">
            <button className="file-selector-button" onClick={removeImage}>
              <Icon type="close" width="24" height="24" />
            </button>
          </Tooltip>
        )}
      </ButtonContainer>
    )

    if (props.type !== 'image' && files.length > 0) {
      return (
        <div className="file-selector-files">
          <div className="file-selector-icon">
            <Tooltip content="Change file">
              <button className="file-selector-button" onClick={changeImage}>
                <Icon type="attachment" width="24" height="24" />
              </button>
            </Tooltip>
          </div>
          {files.map((file, index) => (
            <div key={index}>{file}</div>
          ))}
        </div>
      )
    }

    if (preview) {
      return (
        <>
          <div
            className="file-selector-placeholder-image"
            style={getBackgroundStyle(preview, props.previewStyle)}
          />
          <div className="file-selector-placeholder-overlay">
            <EditButtons />
            {caption}
            {props.helpText && <div>{props.helpText}</div>}
          </div>
        </>
      )
    }

    if (props.dnd) {
      return (
        <>
          <MdPhotoSizeSelectLarge className="file-selector-icon" size={28} />
          <div className="file-selector-text">
            {caption}
            {props.helpText && <div>{props.helpText}</div>}
          </div>
        </>
      )
    }

    return (
      <>
        <div className="file-selector-icon">
          <Tooltip content={props.type === 'image' ? 'Add image' : 'Add file'}>
            <button className="file-selector-button" onClick={changeImage}>
              <Icon
                type={props.type === 'image' ? 'image-add' : 'attachment'}
                width="24"
                height="24"
              />
            </button>
          </Tooltip>
        </div>
        {caption}
        {props.helpText && <div>{props.helpText}</div>}
      </>
    )
  }

  const placeholder = props.dnd
    ? 'Drag files here, or click to browse'
    : 'Choose file'
  const caption = props.caption?.[0] ?? placeholder
  const minHeight = props.compact ? undefined : 200

  const renderWrapper = () => {
    let icon

    if (props.compact) {
      icon = (
        <Icon
          className="file-selector-icon"
          type="attachment"
          width="24"
          height="24"
        />
      )
    }

    if (props.dnd) {
      icon = <MdPhotoSizeSelectLarge className="file-selector-icon" size={28} />
    }

    return (
      <div
        className={cn('file-selector', {
          'file-selector--compact': props.compact,
          'file-selector--dnd': props.dnd,
          'file-selector--rounded': props.dnd && props.rounded,
        })}
        style={{
          minHeight: props.height ?? minHeight,
        }}
        onClick={props.compact || props.dnd ? changeImage : undefined}
      >
        <input
          className="file-selector-input"
          ref={inputRef}
          multiple={props.multiple}
          type="file"
          onChange={handleChange}
        />
        <div className="file-selector-placeholder">
          {props.compact ? (
            <>
              {icon}
              {getCaption(files, caption)}
            </>
          ) : (
            renderUploadArea()
          )}
        </div>
      </div>
    )
  }

  return props.dnd ? (
    <FileDropWrapper multiple={props.multiple} onDrop={onDrop}>
      {renderWrapper()}
    </FileDropWrapper>
  ) : (
    renderWrapper()
  )
}

export default FileSelector
